import type {
  IChatMessage,
  IChatThread,
  IPendingApproval,
  PlanApprovalPayload,
} from './state'

/**
 * Human-in-the-Loop plan approval helpers for the assistant chat.
 *
 * The agents service suspends a run with a `plan_approval` payload when a
 * sub-agent wants the user to confirm a schema / records creation plan.
 * Everything here is pure (no store access) so it can be unit-tested and
 * shared between the initial stream and the resume (continuation) stream.
 */

/** Pending approval found in a thread, with the message that carries the card. */
export interface IPendingApprovalRef extends IPendingApproval {
  messageId: string
}

/**
 * Texts that mean "approve" when typed while a plan card is pending.
 * The keyword must start the message and be a whole word ("окей" is listed
 * next to "ок"; "goal" is not "go"). `\b` is ASCII-only in JavaScript, so a
 * Cyrillic-aware lookahead is used instead of it.
 */
export const APPROVAL_TEXT_RE =
  /^\s*(да|ага|ок|окей|давай|создавай|создать|подтверждаю|yes|ok|okay|approve|go|sure)(?![a-zа-яё0-9_])/i

export const isApprovalText = (text: string): boolean =>
  APPROVAL_TEXT_RE.test(text)

export const isPlanApprovalPayload = (
  value: unknown
): value is PlanApprovalPayload =>
  typeof value === 'object' &&
  value !== null &&
  (value as { kind?: unknown }).kind === 'plan_approval'

/** Newest message of the thread that still carries a plan approval card. */
export const findPendingApproval = (
  threads: IChatThread[],
  threadId?: string
): IPendingApprovalRef | undefined => {
  const thread = threads.find((item) => item.id === threadId)
  if (!thread) return undefined

  for (let index = thread.messages.length - 1; index >= 0; index -= 1) {
    const message = thread.messages[index]
    if (message.pendingApproval) {
      return { messageId: message.id, ...message.pendingApproval }
    }
  }

  return undefined
}

/** Minimal shape of a Mastra stream chunk this module relies on. */
export interface IStreamChunk {
  type: string
  runId?: string
  payload?: any
}

/**
 * Extracts a plan approval from a TOP-LEVEL `tool-call-suspended` chunk.
 * The suspension is raised by a sub-agent, so `payload.toolName` is the
 * delegation tool (`agent-schema` / `agent-objects`), not the inner tool:
 * only `suspendPayload.kind` identifies it. Copies nested inside
 * `tool-output` chunks are deliberately not handled here.
 */
export const getPlanApprovalFromChunk = (
  chunk: IStreamChunk
): IPendingApproval | undefined => {
  if (chunk?.type !== 'tool-call-suspended') return undefined

  const suspendPayload = chunk.payload?.suspendPayload
  if (!isPlanApprovalPayload(suspendPayload)) return undefined
  if (!chunk.runId || !chunk.payload?.toolCallId) return undefined

  return {
    runId: chunk.runId,
    toolCallId: chunk.payload.toolCallId,
    payload: suspendPayload,
  }
}

/** Patch applied to the streaming assistant message; always carries `content`. */
export type IChatMessagePatch = Pick<IChatMessage, 'content'> &
  Partial<Pick<IChatMessage, 'streamingText'>>

/** Receives the effects of stream chunks for one assistant message. */
export interface IChunkSink {
  update(patch: IChatMessagePatch): void
  setPendingApproval(pendingApproval: IPendingApproval): void
  /** Removes the card of this tool call anywhere in the thread. */
  clearPendingApproval(toolCallId?: string): void
}

export interface IChunkHandler {
  (chunk: IStreamChunk): void
  /** Text accumulated so far (used to keep partial output on failures). */
  getContent(): string
}

/**
 * Builds the `onChunk` handler of a Mastra data stream. Text and tool status
 * chunks update the assistant message as before; `tool-call-suspended` with a
 * `plan_approval` payload attaches the approval card and `tool-call-resumed`
 * removes it. Unknown chunk types are ignored.
 */
export const createChunkHandler = (sink: IChunkSink): IChunkHandler => {
  let content = ''

  const setStatus = (streamingText: string | undefined) =>
    sink.update({ content, streamingText })

  const appendText = (text: string) => {
    content += text
    sink.update({ content })
    if (content.trim()) {
      sink.update({ content, streamingText: undefined })
    }
  }

  const handleNested = (chunk: IStreamChunk) => {
    const out = chunk.payload?.output
    if (!out || typeof out !== 'object' || !out.type) return

    const toolName =
      out.payload?.toolName ??
      out.payload?.name ??
      chunk.payload?.toolName ??
      'tool'

    switch (out.type) {
      case 'start':
        setStatus(`Running ${chunk.payload?.toolName ?? 'sub-agent'}...`)
        break
      case 'step-start':
        setStatus('Thinking...')
        break
      case 'text-start':
        setStatus('Writing...')
        break
      case 'text-delta':
        if (out.payload?.text) appendText(out.payload.text)
        break
      case 'text-end':
        setStatus(undefined)
        break
      case 'tool-call':
        setStatus(`Calling tool ${toolName}...`)
        break
      case 'tool-result':
        setStatus(`Tool ${toolName} finished`)
        break
      case 'tool-call-resumed':
        sink.clearPendingApproval(out.payload?.toolCallId)
        break
      case 'step-finish':
      case 'finish':
        setStatus(undefined)
        break
      default:
        break
    }
  }

  const handler = ((chunk: IStreamChunk) => {
    switch (chunk?.type) {
      case 'text-delta':
        appendText(chunk.payload?.text ?? '')
        break
      case 'text-start':
        setStatus('Writing...')
        break
      case 'text-end':
        setStatus(undefined)
        break
      case 'step-start':
        setStatus('Thinking...')
        break
      case 'tool-call-input-streaming-start':
      case 'tool-call':
        setStatus(`Calling tool ${chunk.payload?.toolName ?? 'tool'}...`)
        break
      case 'tool-result':
        setStatus(`Tool ${chunk.payload?.toolName ?? 'tool'} finished`)
        break
      case 'tool-output':
        handleNested(chunk)
        break
      case 'tool-call-suspended': {
        const pendingApproval = getPlanApprovalFromChunk(chunk)
        if (pendingApproval) {
          setStatus(undefined)
          sink.setPendingApproval(pendingApproval)
        }
        break
      }
      case 'tool-call-resumed':
        sink.clearPendingApproval(chunk.payload?.toolCallId)
        break
      case 'error':
        console.error('❌ Stream error:', chunk.payload?.error)
        content = content || `Stream error: ${chunk.payload?.error}`
        sink.update({ content, streamingText: undefined })
        break
      case 'finish':
        setStatus(undefined)
        break
      default:
        break
    }
  }) as IChunkHandler
  handler.getContent = () => content

  return handler
}

/** Subset of `agent.listSuspendedRuns()` items used to restore a card. */
export interface ISuspendedRunLike {
  runId: string
  suspendedAt?: string | Date
  toolCalls?: Array<{
    toolCallId?: string
    toolName?: string
    suspendPayload?: unknown
  }>
}

const toTime = (value?: string | Date): number => {
  if (!value) return 0
  const time = value instanceof Date ? value.getTime() : Date.parse(value)

  return Number.isNaN(time) ? 0 : time
}

/**
 * Newest suspended run that waits for a plan approval. Runs suspended for
 * other reasons (tool approvals, `ask_user`, ...) are ignored.
 */
export const pickPlanApprovalRun = (
  runs: ISuspendedRunLike[] | undefined
): IPendingApproval | undefined => {
  const candidates: Array<{ time: number; pending: IPendingApproval }> = []

  for (const run of runs || []) {
    for (const toolCall of run.toolCalls || []) {
      if (
        toolCall.toolCallId &&
        isPlanApprovalPayload(toolCall.suspendPayload)
      ) {
        candidates.push({
          time: toTime(run.suspendedAt),
          pending: {
            runId: run.runId,
            toolCallId: toolCall.toolCallId,
            payload: toolCall.suspendPayload,
          },
        })
      }
    }
  }

  if (!candidates.length) return undefined

  candidates.sort((a, b) => b.time - a.time)

  return candidates[0].pending
}
